import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, Paper, Grid, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, IconButton, Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Bookmark as BookmarkIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';
import axios from '../api/axios';
import Navigation from '../components/Navigation';

const fmtCurrency = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const TemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [homeowners, setHomeowners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [useDialog, setUseDialog] = useState(null); // selected template object
  const [newName, setNewName] = useState('');
  const [newHomeownerId, setNewHomeownerId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, hRes] = await Promise.all([
        axios.get('/api/projects/templates'),
        axios.get('/api/projects/homeowners').catch(() => ({ data: { homeowners: [] } })),
      ]);
      setTemplates(tRes.data.templates || []);
      setHomeowners(hRes.data.homeowners || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openUseDialog = (template) => {
    setUseDialog(template);
    setNewName(`${template.name} — copy`);
    setNewHomeownerId('');
  };

  const handleCreateFromTemplate = async () => {
    if (!useDialog) return;
    if (!newName.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(`/api/projects/from-template/${useDialog.id}`, {
        name: newName.trim(),
        homeowner_id: newHomeownerId || null,
      });
      const newId = response.data?.estimate?.id;
      setUseDialog(null);
      if (newId) navigate(`/estimate/${newId}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create estimate from template.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"? Estimates created from it are not affected.`)) return;
    try {
      await axios.delete(`/api/projects/${template.id}`);
      setSuccess('Template deleted');
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete template.');
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Navigation showNotifications={false} showLogout={true} />

      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate(-1)} aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <BookmarkIcon color="primary" />
          <Typography variant="h4">Estimate Templates</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={3}>
          Templates are reusable estimate skeletons. Save any estimate as a template, then start
          new ones from it with one click — rooms, items, costs and dimensions copied over fresh.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <InboxIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" gutterBottom>No templates yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Submit an estimate and answer "yes" when asked to save it as a template, or use the
              "Save as Template" prompt in the estimate builder.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {templates.map((t) => (
              <Grid item xs={12} sm={6} md={4} key={t.id}>
                <Paper elevation={2} sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom>{t.name}</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    <Chip label={`${t.room_count} room${t.room_count === '1' ? '' : 's'}`} size="small" />
                    <Chip label={`${t.item_count} item${t.item_count === '1' ? '' : 's'}`} size="small" />
                    <Chip label={fmtCurrency(t.total_cost)} size="small" color="primary" />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                    Last updated {new Date(t.updated_at).toLocaleDateString()}
                  </Typography>
                  <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => openUseDialog(t)}
                      fullWidth
                    >
                      Use
                    </Button>
                    <IconButton color="error" onClick={() => handleDelete(t)} aria-label="Delete template">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <Dialog open={!!useDialog} onClose={() => setUseDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Start estimate from "{useDialog?.name}"</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We'll create a fresh estimate with this template's rooms, items, and costs. Approvals
            reset to outstanding so the homeowner can review.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="New estimate name"
            fullWidth
            variant="outlined"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControl fullWidth variant="outlined">
            <InputLabel>Assign homeowner (optional)</InputLabel>
            <Select
              value={newHomeownerId}
              onChange={(e) => setNewHomeownerId(e.target.value)}
              label="Assign homeowner (optional)"
            >
              <MenuItem value=""><em>Leave unassigned (assign later)</em></MenuItem>
              {homeowners.map((h) => (
                <MenuItem key={h.id} value={h.id}>
                  {h.first_name} {h.last_name} ({h.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUseDialog(null)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleCreateFromTemplate}
            variant="contained"
            disabled={!newName.trim() || submitting}
          >
            {submitting ? 'Creating…' : 'Create Estimate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplatesPage;
